import ImageResource from './image'
import CalendarTypeResource from './calendartype'
import SocketResource from './socketResource'
import ReservationResource from './reservation'
import LocationResource from './location'

export default class CalendarResource extends SocketResource {
    static endpoint = '/calendars'
    static socketPath = 'calendars'
    static socketLabel = 'calendar'

    static related() {
        return {
            image: ImageResource,
            type: CalendarTypeResource,
            location: LocationResource,
        }
    }

    static async hasSlotAvailable(slot) {
        return this.filter({ has_slot_avail: slot })
    }

    static async hasSlotAvailableAll(slot, block_count = 1) {
        return this.filterAll({ has_slot_avail: slot, block_count })
    }

    async getQuote(date) {
        let wrapped = this.wrap('/avail')
        return wrapped.get({ params: { date } }).then((response) => {
            let resources = []

            for (let key in response.data) {
                resources.push(new ReservationResource(response.data[key]))
            }

            return resources
        })
    }

    static async parallel(date, block, location, options = {}) {
        const opts = { sessions: 1, racers: 1, deal: null, ...options }
        const params = { date, block, location, sessions: opts.sessions, racers: opts.racers }
        if (opts.deal) {
            params.deal = opts.deal
        }
        const result = await this.wrap('/parallel').get({ params })
        let reservations = []

        for (const item of result.data) {
            for (const slotItem of item.slots) {
                reservations.push(new ReservationResource({ slot: slotItem.slot, time: slotItem.time, calendar: item.calendar, price_cents: slotItem.price_cents, pricing_rule: slotItem.pricing_rule }))
            }
        }

        return reservations
    }

    async seated() {
        const result = await this.wrap('/seated').get()

        if (result.status == 200) {
            return result.data
        }
    }

    // Asks whichever shiftos-launcher is connected on this seat's calendar channel to
    // immediately report its status (acsRunning, downloadingContent, etc) rather than
    // waiting for it to push one on its own -- see shift_core.py/screen/app.js's
    // handleCalendarMessage(). Useful right after connecting, since the launcher only
    // otherwise sends 'status' at its own startup or when a watched value changes, so a
    // page that opens afterward would otherwise show stale/unknown state until the next
    // unrelated change on the launcher side.
    socketSendStatusRequest() {
        this._send({ type: 'status_request' })
    }

    // Race Control switched to the "Racers" tab -- ask the sim on this seat's
    // channel to re-emit its current selection (car / user / assists) so the tab
    // reflects reality without waiting for the next change. Handled in
    // shift_core.py/screen/app.js's handleCalendarMessage().
    socketSendRacerStateQuery() {
        this._send({ type: 'racer_state_query' })
    }

    // Race Control switched to the "Sessions" tab -- ask the host sim to re-emit
    // its session configuration.
    socketSendSessionQuery() {
        this._send({ type: 'session_query' })
    }

    createReservation(attributes) {
        let attrs = Object.assign(attributes, { calendar: this.id })
        return new ReservationResource(attrs)
    }

    costCents() {
        return Number(this.attributes.cost_cents)
    }

    toString() {
        return this.attributes.name
    }
}
